import { DEFAULT_OG_IMAGE, SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { Suspense, useMemo } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CollectionChips } from "../components/collection-chips";
import { hasCollections, useCollectionLabels, useCuisineLabels } from "../lib/hub-collections";
import { Container } from "../../../shared/ui/container";
import { useActivities } from "../hooks/use-activities";
import { BOOKABLE_FIRST_HUBS, buildActivityFilters, parseShowFilter } from "../api/activities-query";
import { HubTabRail, HubTitleRow, HubIntro, HubSlide, HubFade } from "../components/hub-top";
import { useIsDesk } from "../../../shared/hooks/use-is-desk";
import { HubShowMoreGrid } from "../components/hub-show-more";
import { HubCityButton, HubFiltersInline, HubFloatingFilters, type FilterChipGroup, type HubFilterValue } from "../components/hub-dialogs";
import { StaysHubSearch } from "../../stays/components/stays-hub-search";
import { StaysHubGrid } from "../../stays/components/stays-hub-grid";
import { StaysMapToggle } from "../../stays/components/stays-map-toggle";
import { STAYS_MAP_AVAILABLE } from "../../stays/components/stays-hub-map-lazy";
import { useRememberedMap } from "../../stays/lib/use-remembered-map";
import { readVibes, vibesParam, type StayVibe } from "../../stays/lib/vibes";
import {
  countStayFilters,
  inPriceRange,
  matchesStayFilters,
  readStayFilters,
  stayFilterParams,
  stayPriceBounds,
  type StayFilterSearch,
} from "../../stays/lib/stay-filters";
import { ALL_STAY_TYPES, useStaysByType } from "../../stays/api/stays-query";
import { parseChildAges } from "../../stays/lib/nights";
import { nightsBetween, useNightlyPrice } from "../../stays/lib/nightly";
import { campHasVibes, stayHasVibes } from "../../stays/lib/vibes";
import { useCurrency } from "../../../lib/currency";
import { SubcategoryBar } from "../components/subcategory-bar";
import { CityCategoryGroups } from "../components/city-category-groups";
import { FallbackSuggestions } from "../components/fallback-suggestions";
import { EmptyCityHelp } from "../components/empty-city-help";
import { isEmptyCityHubView } from "../lib/nearby-bookable-cities";
import { CategoryFaq } from "../components/category-faq";
import { CategoryIntro } from "../components/category-intro";
import { CategoryNeighborhoods } from "../components/category-neighborhoods";
import { CategoryNearbyCities } from "../components/category-nearby-cities";
import { CityExploreLinks } from "../components/city-explore-links";
import { SEGMENT_LABEL_KEY, isUnionHubSlug } from "../lib/explore-segments";
import { CategoryCityArticles } from "../components/category-city-articles";
import { CategoryRelated } from "../components/category-related";
import { buildCategorySeo, buildJsonLd } from "../seo/category-seo";
import { useCategoryBySlug, useCityBySlug } from "../hooks/use-seo-lookup";
import { useCategories } from "../hooks/use-categories";
import { useCities } from "../hooks/use-cities";
import { citiesInListing, stockedCities, stockedRootCategories, type CategoryCountRow, type CityCountRow } from "../lib/hub-inventory";
import { useResultsAnchor } from "../hooks/use-results-anchor";
import { formatMoroccoLocation, getMoroccoName } from "../../../shared/lib/locale";
import { OG_LOCALE, RESTAURANT_CUISINE_IDS, SUPPORTED_LANGS, hubCollections, isStayPropertyType, type Lang } from "@majourneys/shared";

import { Icon } from "@majourneys/icons";
import { HubBookingBar, showBookingBar } from "../components/hub-booking-bar";
import { HubTonightChips } from "../components/hub-tonight-chips";
import { HubResultsLine } from "../components/hub-results-line";
import { OPEN_HOURS_HUBS, applyChipsSearch, bookingLinkSearch, readHubWhen } from "../lib/hub-when";
import { openStateAt, useClientNow } from "../hooks/use-client-now";
/** What the price slider spans. Also the ceiling a filtered URL reads as "no
 *  upper bound" when only a minimum was set. */
const PRICE_BOUNDS = { min: 0, max: 1000 };

interface RouteParams { lang: string; category?: string; city?: string }
/* page and the prices are numbers in the URL, but a link written before that
   (or by hand) still arrives as a string, so both are accepted on the way in. */
interface RouteSearch extends StayFilterSearch { q?: string; page?: string | number; min_price?: string | number; max_price?: string | number; sort?: string; show?: string; checkin?: string; checkout?: string; adults?: string | number; vibe?: string; tag?: string; cuisine?: string; date?: string; time?: string; open?: string | number; tonight?: string | number }

/** Hubs that offer the map. */
const MAP_HUBS = new Set(["stays", "activities", "pools", "restaurants", "spas"]);

const SORT_OPTIONS = [
  { value: "", key: "sort.recommended", label: "Recommended" },
  { value: "price_asc", key: "sort.price_asc", label: "Price: Low to High" },
  { value: "price_desc", key: "sort.price_desc", label: "Price: High to Low" },
  { value: "newest", key: "sort.newest", label: "Newest" },
];

function ExploreContent() {
  const params = useParams({ strict: false }) as RouteParams;
  const search = useSearch({ strict: false }) as RouteSearch;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = params.lang ?? "en";
  const langSwap = new RegExp(`/(${SUPPORTED_LANGS.join("|")})(/|$)`);

  const searchQuery = search.q?.trim() || undefined;
  const isCityOverview = !!params.city && !params.category && !searchQuery;
  const filters = buildActivityFilters(params, search);

  const { data: response } = useActivities(filters);
  const list = response.data;

  // The "when" of the page (#62, #65): day, time, guests, Open now, Tables
  // tonight. Only on the hubs whose venues carry opening hours.
  const hoursHub = !!params.category && OPEN_HOURS_HUBS.has(params.category);
  // Rows on this page that can be reserved through us. Restaurants and spas
  // list bookable rows first, so a first page with none means the hub has
  // none (Fez restaurants are all guide-only).
  const bookableCount = list.filter((a) => (a as { mode?: string }).mode === "bookable").length;
  const when = readHubWhen(search as Record<string, unknown>);
  // Day, time and guests only mean something where a table can be booked, so
  // the bar goes when the hub lists places and none of them is bookable. It
  // stays while a day, time or guest count is set, or the list is empty: a
  // filter that matched nothing must still be changeable from the bar.
  const whenActive = !!(when.date || when.time || when.adults || when.open || when.tonight);
  const guideOnlyHub = list.length > 0 && bookableCount === 0 && !whenActive;
  const barOn = showBookingBar(params.category) && !guideOnlyHub;
  // What the hub copy may promise. A day/time filter that hid every bookable
  // row says nothing about the hub, so the count is left unknown then.
  const seoBookableCount = whenActive && bookableCount === 0 ? undefined : bookableCount;
  // Bookable cards carry the bar's day, time and guests into the venue's
  // booking sheet (?book=1&date=&time=&adults=).
  const linkSearch = hoursHub ? bookingLinkSearch(when) : undefined;
  // "Open now" is filtered by the server, but a cached page can be minutes
  // old: after mount the browser re-checks each venue on Morocco's clock and
  // drops one that has closed since. Before mount it is the server's list, so
  // the hydrated HTML matches.
  const clientNow = useClientNow(60_000, hoursHub && when.open);
  const gridList = useMemo(
    () =>
      hoursHub && when.open && clientNow
        ? list.filter((a) => openStateAt((a as { openingHours?: unknown }).openingHours, clientNow) !== "closed")
        : list,
    [hoursHub, when.open, clientNow, list],
  );
  // The quick chips only where the hours exist in this list (or one is on).
  const hasHours = list.some((a) => !!(a as { openingHours?: unknown }).openingHours);
  const showQuickChips = hoursHub && (hasHours || when.open || when.tonight);
  const page = response.page;
  const totalPages = response.totalPages;
  const total = (response as { total?: number }).total ?? list.length;

  // All / Bookable, on union hubs only.
  const isUnionHub = !!params.category && isUnionHubSlug(params.category);
  const showFilter = isUnionHub ? parseShowFilter(search.show) : undefined;
  // Render the control only when it can do something. With no filter applied
  // the grid holds the hub's whole inventory, so counting modes in it answers
  // "are there two halves here?" without a second request — a Marrakech stays
  // hub with 5 explore rows and no bookable ones gets no control. Once a
  // filter IS applied the list is narrowed by definition, so the control stays
  // put; otherwise picking "Bookable" would remove the only way back to All.
  // A city hub (or city + category hub) with nothing in it at all. Only when
  // nothing narrows the list: a search or a price range that matches nothing
  // says nothing about the city, so those keep the ordinary fallback.
  const isEmptyCityHub = isEmptyCityHubView({
    city: params.city,
    category: params.category,
    total,
    q: searchQuery,
    minPrice: search.min_price,
    maxPrice: search.max_price,
    // "All" widens the list rather than narrowing it.
    show: showFilter === "all" ? undefined : showFilter,
  });

  // Restaurants and Spas list bookable venues by default; the API says how
  // many guide-only ones that left out, which is what "All" would add.
  const isBookableFirstHub = !!params.category && BOOKABLE_FIRST_HUBS.has(params.category);
  const hiddenUnbookable = (response as { hiddenUnbookable?: number }).hiddenUnbookable ?? 0;
  const showAudienceFilter =
    isUnionHub &&
    isBookableFirstHub &&
    (showFilter !== undefined || hiddenUnbookable > 0);

  // One origin on the server and in the browser: canonical and JSON-LD must
  // reach Google in the SSR HTML, and a window-derived origin made the
  // client render a different URL from the server (and a localhost one in dev).
  const origin = SITE_ORIGIN;

  const { data: cityRow } = useCityBySlug(params.city);
  const { data: categoryRow } = useCategoryBySlug(params.category);

  const cityOverride = useMemo(() => {
    // The page's own locale only: falling back to the English block put
    // English taglines and "best time" text on /es /it /de /nl hubs.
    const seo = cityRow?.seo as Record<string, Record<string, unknown> | undefined> | undefined;
    const block = seo?.[lang];
    return block && Object.keys(block).length ? block : undefined;
  }, [cityRow, lang]);

  const categoryOverride = useMemo(() => {
    const seo = categoryRow?.seo as Record<string, Record<string, unknown> | undefined> | undefined;
    const block = seo?.[lang];
    return block && Object.keys(block).length ? block : undefined;
  }, [categoryRow, lang]);

  const cityNameOverride = useMemo(() => {
    const name = cityRow?.name as Record<string, string | undefined> | undefined;
    return name?.[lang] || name?.en || undefined;
  }, [cityRow, lang]);

  const categoryNameOverride = useMemo(() => {
    const name = categoryRow?.name as Record<string, string | undefined> | undefined;
    const fromDb = name?.[lang] || name?.en;
    if (fromDb) return fromDb;
    // Segment hubs have no categories row, so the SEO builder would fall back
    // to titleising the slug — and the slug is the internal name. "Locations
    // in Fez" is both wrong English and the wrong search term; the page has to
    // say "Attractions in Fez". These labels already ship in all 7 locales.
    const key = params.category ? SEGMENT_LABEL_KEY[params.category] : undefined;
    return key ? t(key) : undefined;
  }, [categoryRow, lang, params.category, t]);

  // Unique hub intro authored on the category row (description column /
  // translations.<lang>.description). Only for country-level category hubs —
  // city-scoped hubs keep the templated city lede.
  const introOverride = useMemo(() => {
    if (!params.category || params.city) return undefined;
    const description = categoryRow?.description as
      | Record<string, string | undefined>
      | undefined;
    const text = description?.[lang] || description?.en;
    return text?.trim() ? text.trim() : undefined;
  }, [categoryRow, lang, params.category, params.city]);

  // What the cross-links may point at: only hubs that hold something, since an
  // empty one is noindex. A city page offers the city's stocked categories; a
  // category page the cities its own listing has places in.
  const { data: categoriesResponse } = useCategories(params.city);
  const { data: citiesResponse } = useCities();
  const cityRows = (citiesResponse.data ?? []) as CityCountRow[];
  const relatedInventory = useMemo(() => {
    const categories = stockedRootCategories((categoriesResponse.data ?? []) as CategoryCountRow[]);
    const cities = params.category && !params.city ? citiesInListing(list) : stockedCities(cityRows);
    return { categories, cities };
  }, [categoriesResponse, cityRows, list, params.category, params.city]);

  const seo = useMemo(
    () =>
      buildCategorySeo({
        categorySlug: params.category,
        citySlug: params.city,
        resultCount: total,
        page,
        origin,
        locale: lang,
        cityOverride,
        categoryOverride,
        cityNameOverride,
        categoryNameOverride,
        introOverride,
        relatedInventory,
        bookableCount: seoBookableCount,
      }),
    [
      params.category,
      params.city,
      total,
      page,
      origin,
      lang,
      cityOverride,
      categoryOverride,
      cityNameOverride,
      categoryNameOverride,
      introOverride,
      relatedInventory,
      seoBookableCount,
    ],
  );

  const jsonLd = useMemo(() => {
    // Enrich items with city/category/segment so JSON-LD ItemList URLs are
    // canonical 4-segment, not the legacy 2-segment short URLs that 301.
    const enriched = list.map((it) => {
      const item = it as unknown as {
        slug: string;
        title?: unknown;
        images?: string[];
        price?: number;
        rating?: number;
        city?: { slug?: string | null } | null;
        category?: { slug?: string | null } | null;
        categorySlug?: string | null;
        citySlug?: string | null;
        segment?: string | null;
      };
      return {
        slug: item.slug,
        title: typeof item.title === "string" ? item.title : "",
        images: item.images,
        price: item.price,
        rating: item.rating,
        citySlug: item.city?.slug ?? item.citySlug ?? null,
        categorySlug: item.category?.slug ?? item.categorySlug ?? null,
        segment: item.segment ?? null,
      };
    });
    return buildJsonLd(seo, enriched, origin);
  }, [seo, list, origin]);


  /**
   * Page, price and sort go into the URL as the router's own types — numbers
   * as numbers.
   *
   * A string that parses as JSON is re-encoded on the way out, so `"5"` became
   * the literal `?page="5"` in the address bar and in every link a guest
   * copied out of it. Page 1 drops the param rather than writing `?page=1`,
   * which would be a second URL for a page that already has a canonical one.
   */
  const noPriceHub = params.category === "restaurants";
  const activeSort = search.sort ?? "";

  /* The Stays hub's map, off until the guest asks for it (owner, 2026-09-28).
     Page state rather than a URL param: it changes how the list is shown, not
     which stays are in it, so it earns no second URL for Google to weigh. */
  const [mapWanted, setMapWanted] = useRememberedMap();
  // The map (owner, 2026-09-28): Stays first, then the hubs where the place
  // itself is the point: activities, pools, restaurants, spas.
  const canMap = MAP_HUBS.has(params.category ?? "") && STAYS_MAP_AVAILABLE;
  const mapOn = canMap && mapWanted;
  const isDesk = useIsDesk();

  const stayFilters = useMemo(
    () => (params.category === "stays" ? readStayFilters(search) : {}),
    // Keyed on the params it reads, not the whole search object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.category, search.stars, search.amen, search.near, search.meal, search.free_cancel, search.pool, search.breakfast],
  );
  const stayDatesChosen = !!(search.checkin && search.checkout);
  const isStays = params.category === "stays";
  const { currency } = useCurrency();
  const priceBounds = isStays ? stayPriceBounds(currency) : PRICE_BOUNDS;
  const priceMin = search.min_price !== undefined && search.min_price !== "" ? Number(search.min_price) : undefined;
  const priceMax = search.max_price !== undefined && search.max_price !== "" ? Number(search.max_price) : undefined;

  /* The same list the stays grid reads (same query key, so no second fetch),
     for the live counts in the Filters panel. Off on every other hub. */
  const stayAdults = search.adults != null && search.adults !== "" ? Number(search.adults) : undefined;
  const stayChildAges = useMemo(
    () => parseChildAges((search as { childAges?: string }).childAges),
    [(search as { childAges?: string }).childAges],
  );
  const { data: stayData } = useStaysByType(
    isStays ? ALL_STAY_TYPES : "",
    { checkin: search.checkin, checkout: search.checkout, adults: stayAdults, childAges: stayChildAges },
    params.city,
  );
  const nightlyOf = useNightlyPrice(nightsBetween(search.checkin, search.checkout));
  const countStays = (staged: ReturnType<typeof readStayFilters>, price: { min?: number; max?: number }) => {
    const hotels = (stayData?.properties ?? []).filter(
      (p) =>
        matchesStayFilters(p, staged) &&
        inPriceRange(nightlyOf(p), price.min, price.max) &&
        stayHasVibes(p.amenities, vibes),
    ).length;
    // Camps have no stars or amenities to test, so any stay filter leaves
    // them out, as the grid does; the price range still applies to them.
    const camps =
      countStayFilters(staged) === 0 && campHasVibes(vibes)
        ? list.filter((c) => inPriceRange(c.price || null, price.min, price.max)).length
        : 0;
    return hotels + camps;
  };
  // Restaurants and spas hub chips. The tag travels in the URL (so a
  // shared link keeps it) and resets to page 1, like every other filter.
  const withCollections = hasCollections(params.category);
  const collectionLabels = useCollectionLabels(params.category);
  const tagCounts = (response as { tagCounts?: Record<string, number> }).tagCounts;
  const setTag = (next: string | undefined) => {
    navigate({
      search: ((prev: Record<string, unknown>) => {
        const { tag: _drop, page: _page, ...rest } = prev;
        return next ? { ...rest, tag: next } : rest;
      }) as never,
      resetScroll: false,
    });
  };
  const vibes = params.category === "stays" ? readVibes(search.vibe) : [];
  // The cuisine row on the restaurants hub: its own param, so it combines
  // with a collection. Like the tag, a change goes back to page 1.
  const withCuisines = params.category === "restaurants";
  const cuisineLabels = useCuisineLabels();
  const setCuisine = (next: string | undefined) => {
    navigate({
      search: ((prev: Record<string, unknown>) => {
        const { cuisine: _drop, page: _page, ...rest } = prev;
        return next ? { ...rest, cuisine: next } : rest;
      }) as never,
      resetScroll: false,
    });
  };
  const setVibes = (next: StayVibe[]) => {
    const value = vibesParam(next);
    navigate({
      search: ((prev: Record<string, unknown>) => {
        const { vibe: _drop, ...rest } = prev;
        return value ? { ...rest, vibe: value } : rest;
      }) as never,
      resetScroll: false,
    });
  };

  /** Price and sort land in one navigation, from the Filters popup. */
  function applyHubFilters(next: HubFilterValue) {
    navigate({
      search: ((prev: Record<string, unknown>) => {
        const out = { ...prev } as Record<string, unknown>;
        delete out.page;
        if (next.min != null && next.max != null) {
          out.min_price = next.min;
          out.max_price = next.max;
        } else {
          delete out.min_price;
          delete out.max_price;
        }
        if (next.sort) out.sort = next.sort;
        else delete out.sort;
        if (next.show) out.show = next.show;
        else delete out.show;
        // The Filters sheet's chip groups (#54). Open now and Tables tonight
        // are flags written as 1; a collection is its id.
        const withChips = next.chips ? applyChipsSearch(out, next.chips) : out;
        if (next.stay) {
          for (const [key, v] of Object.entries(stayFilterParams(next.stay))) {
            if (v === undefined) delete withChips[key];
            else withChips[key] = v;
          }
        }
        return withChips;
      }) as never,
      resetScroll: false,
    });
  }

  const countBase = useMemo(() => {
    const { page: _p, page_size: _ps, min_price: _a, max_price: _b, sort: _s, ...rest } = filters;
    return rest;
  }, [filters]);

  // Restaurant tables are free to book, so there is no price to sort by there
  // (owner, 2026-10-01).
  const sortOptions = SORT_OPTIONS
    .filter((o) => !noPriceHub || !o.value.startsWith("price_"))
    .map((o) => ({ value: o.value, label: t(o.key, { defaultValue: o.label }) }));

  // #54: the page's own filter groups as chips in the Filters sheet, on the
  // hubs with opening hours: when (Open now, Tables tonight), then the
  // restaurant collections this city has.
  const chipGroups = useMemo<FilterChipGroup[] | undefined>(() => {
    if (!hoursHub || !params.category) return undefined;
    const groups: FilterChipGroup[] = [];
    if (showQuickChips) {
      groups.push({
        title: t("explore.quick.group"),
        options: [
          // "Tables tonight" is no longer offered (owner, 2026-10-14).
          { param: "open", value: "1", label: t("explore.quick.open_now") },
        ],
      });
    }
    if (withCuisines) {
      const cuisines = RESTAURANT_CUISINE_IDS.filter((id) => id === filters.cuisine || (tagCounts?.[id] ?? 0) > 0);
      if (cuisines.length > 0) {
        groups.push({
          title: cuisineLabels.groupLabel,
          options: cuisines.map((id) => ({ param: "cuisine", value: id, label: cuisineLabels.label(id) })),
        });
      }
    }
    if (withCollections) {
      const collections = hubCollections(params.category).filter((c) => c.id === filters.tag || (tagCounts?.[c.id] ?? 0) > 0);
      if (collections.length > 0) {
        groups.push({
          title: collectionLabels.groupLabel,
          options: collections.map((c) => ({ param: "tag", value: c.id, label: collectionLabels.label(c.id) })),
        });
      }
    }
    return groups.length > 0 ? groups : undefined;
  }, [hoursHub, params.category, showQuickChips, withCollections, withCuisines, filters.tag, filters.cuisine, tagCounts, collectionLabels, cuisineLabels, t]);

  /** The list's filters for staged chips: the same builder as the grid, so the count matches it. */
  const countFor = (chips: Record<string, string | undefined>) => {
    const staged = buildActivityFilters(params, {
      ...(search as Record<string, unknown>),
      open: chips.open,
      tonight: chips.tonight,
      tag: chips.tag,
      cuisine: chips.cuisine,
    });
    const { page: _p, page_size: _ps, min_price: _a, max_price: _b, sort: _s, ...rest } = staged;
    return rest;
  };

  /* Every input that changes which venues are listed. Whenever one of them
     moves, the list scrolls back under the header. */
  const resultsRef = useResultsAnchor(
    [
      params.category ?? "",
      params.city ?? "",
      searchQuery ?? "",
      search.page ?? "1",
      // On Stays, sort, price, filters, moods and the map are changed from
      // inside the list, often from the pinned bar, and StaysHubGrid keeps the
      // guest's place for those itself. Anywhere else they re-anchor here.
      ...(params.category === "stays"
        ? []
        : [
            activeSort,
            search.min_price ?? "",
            search.max_price ?? "",
            search.show ?? "",
            when.date ?? "",
            when.time ?? "",
            when.open ? "open" : "",
            when.tonight ? "tonight" : "",
            // Map on or off reflows a desk's list beside the map, so land on
            // it. A phone's map is a layer over the list, which never moves.
            mapOn && isDesk ? "map" : "list",
          ]),
    ].join("|"),
  );

  return (
    // The page tone the pewter panels sit in: cool off-white the whole way
    // down. The old gradient ended in peach, which put a warm cast under a
    // grid of cool white cards.
    //
    // `overflow-x-clip`, not `hidden`: the sticky rail's fill is a `w-screen`
    // layer, and 100vw includes the scrollbar on platforms that reserve one,
    // so without a clip it would add a horizontal scrollbar. `clip` creates no
    // scroll container, so the rail below still sticks to the viewport --
    // `overflow-x: hidden` here would silently kill that.
    <main className="relative overflow-x-clip bg-[linear-gradient(180deg,#ffffff_0%,#f7f7fb_45%,#f2f3f8_100%)]">
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords.length > 0 && <meta name="keywords" content={seo.keywords.join(", ")} />}
      <meta
        name="robots"
        content={
          // A filtered view is a subset of a hub that is already indexed, so
          // it stays out of the index while still passing link equity through.
          // `follow` matters: the venues reachable only from a filtered view
          // must not become unreachable because of it. The canonical below
          // ignores `show` and keeps pointing at the full hub.
          // A Stays hub counts its hotels too: camps are only part of it, and
          // a city with 90 riads and no camp is not an empty page.
          total + (isStays ? (stayData?.properties.length ?? 0) : 0) === 0 || showFilter
            ? "noindex,follow"
            : "index,follow,max-image-preview:large,max-snippet:-1"
        }
      />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="MaJourneys" />
      <meta property="og:locale" content={OG_LOCALE[lang as Lang] ?? OG_LOCALE.en} />
      <meta property="og:url" content={seo.canonical} />
      <link rel="canonical" href={seo.canonical} />
      {SUPPORTED_LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={seo.canonical.replace(langSwap, `/${l}$2`)}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={seo.canonical.replace(langSwap, "/en$2")}
      />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      {seo.cityName && <meta name="geo.placename" content={formatMoroccoLocation(seo.cityName, lang)} />}
      <meta name="geo.region" content="MA" />
      <meta name="geo.country" content={getMoroccoName(lang)} />

      {jsonLd.map((doc, i) => (
        <script
          // eslint-disable-next-line react/no-danger
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(doc) }}
        />
      ))}

      {/* The phone's icon tabs, pinned under the site header. */}
      <Suspense fallback={<div className="h-[78px]" aria-hidden />}>
        <HubTabRail lang={lang} activeSlug={params.category} citySlug={params.city} />
      </Suspense>

      <Container>
        <HubSlide k={params.category ?? "all"}>
        <div className="relative" ref={resultsRef}>
          {/* Types, city and Filters: stacked on a phone, one row on a desk
              (owner, 2026-09-26, layout A). */}
          <div className="pt-5 lg:flex lg:items-center lg:gap-6">
            <div className="min-w-0 lg:flex-1">
              {/* The types sit right under the category tabs (owner, 2026-09-26). */}
              {params.category && (
                <Suspense fallback={null}>
                  <SubcategoryBar categorySlug={params.category} citySlug={params.city} lang={lang} />
                </Suspense>
              )}
            </div>
            {barOn ? (
              // The bar below carries the city picker, List / Map and Filters.
              <h1 className="sr-only">{seo.h1}</h1>
            ) : (
            <HubTitleRow
              lang={lang}
              h1={seo.h1}
              categoryName={seo.categoryName}
              cityName={seo.cityName}
              total={total}
              controls={
                <>
                  <Suspense fallback={null}>
                    {/* Stays searches by dates and party (LiteAPI); every
                        other hub picks a city. */}
                    {params.category === "stays" ? (
                      <StaysHubSearch lang={lang} citySlug={params.city} search={search as { checkin?: string; checkout?: string; adults?: number; childAges?: string }} />
                    ) : (
                      <HubCityButton
                        lang={lang}
                        categorySlug={params.category}
                        categoryName={seo.categoryName}
                        citySlug={params.city}
                        countFilters={filters}
                      />
                    )}
                  </Suspense>
                  <HubFiltersInline />
                  {canMap ? <StaysMapToggle on={mapOn} onChange={setMapWanted} variant="inline" /> : null}
                </>
              }
            />
            )}
          </div>

          {barOn && params.category ? (
            <HubBookingBar
              lang={lang}
              category={params.category}
              search={search as Record<string, unknown>}
              where={
                <Suspense fallback={null}>
                  <HubCityButton
                    lang={lang}
                    categorySlug={params.category}
                    categoryName={seo.categoryName}
                    citySlug={params.city}
                    countFilters={filters}
                  />
                </Suspense>
              }
              map={canMap ? { on: mapOn, onChange: setMapWanted } : undefined}
            />
          ) : null}



          {withCollections || showQuickChips ? (
            <div className="pt-4">
              {withCuisines ? (
                <CollectionChips
                  category={params.category}
                  ids={RESTAURANT_CUISINE_IDS}
                  labels={cuisineLabels}
                  counts={tagCounts}
                  active={filters.cuisine}
                  onChange={setCuisine}
                  testId="restaurant-cuisines"
                />
              ) : null}
              <CollectionChips
                category={params.category}
                counts={tagCounts}
                active={filters.tag}
                onChange={setTag}
                collections={withCollections}
                leading={
                  showQuickChips && params.category ? (
                    <HubTonightChips category={params.category} search={search as Record<string, unknown>} />
                  ) : undefined
                }
              />
            </div>
          ) : null}

          {hoursHub && params.category && !isEmptyCityHub ? (
            <HubResultsLine
              category={params.category}
              total={total}
              lang={lang}
              search={search as Record<string, unknown>}
              sortOptions={sortOptions}
            />
          ) : null}

          {/* The dark "Hotels you book online" panel is off this page (owner,
              2026-09-25). Hotels, riads, resorts and villas are reached
              through their own tiles in the type row above. */}

          {/* A submitted search says what it searched for, with a way back
              to the whole page (the header's Enter lands here). */}
          {searchQuery && params.category !== "stays" ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-5" data-testid="search-results-for">
              <p role="status" className="min-w-0 break-words text-[15px] font-semibold text-[#110E39]">
                {t("explore.search_results_for", { query: searchQuery })}
              </p>
              <Link
                to="."
                // Drops the words and the page they were on (a deliberate
                // reset); city, category, price, sort and the rest stay.
                search={((prev: Record<string, unknown>) => {
                  const { q: _q, page: _p, ...rest } = prev;
                  return rest;
                }) as never}
                className="inline-flex min-h-[24px] items-center text-[13px] font-semibold text-[#5a5d77] underline underline-offset-2"
              >
                {t("explore.search_clear")}
              </Link>
            </div>
          ) : null}

          <HubFade k={params.city ?? "all"}>
          <div className="py-8">
            {params.category === "stays" ? (
              // Camps and every LiteAPI property together (StaysHubGrid). Never
              // the fallback: a city with no camp can still have 40 riads.
              <StaysHubGrid
                camps={list}
                citySlug={params.city}
                lang={lang}
                search={search as { checkin?: string; checkout?: string; adults?: number; childAges?: string }}
                mapOn={mapOn}
                filters={stayFilters}
                vibes={vibes}
                onVibesChange={setVibes}
                priceRange={{ min: priceMin, max: priceMax }}
                onMapChange={canMap ? setMapWanted : undefined}
                stickyBar={{
                  activeType: "stays",
                  place: seo.cityName ?? null,
                  carriedSearch: {
                    ...(search.checkin ? { checkin: search.checkin } : {}),
                    ...(search.checkout ? { checkout: search.checkout } : {}),
                    ...(search.adults != null ? { adults: search.adults } : {}),
                  },
                }}
              />
            ) : isEmptyCityHub && params.city ? (
              <EmptyCityHelp
                lang={lang}
                citySlug={params.city}
                cityName={seo.cityName ?? params.city}
                categoryName={params.category ? seo.categoryName ?? undefined : undefined}
                categorySlug={params.category}
              />
            ) : list.length === 0 ? (
              <FallbackSuggestions
                query={searchQuery}
                lang={lang}
                categorySlug={params.category}
                citySlug={params.city}
                categoryName={seo.categoryName ?? undefined}
                cityName={seo.cityName ?? undefined}
              />
            ) : isCityOverview && params.city ? (
              <CityCategoryGroups
                items={list}
                lang={lang}
                citySlug={params.city}
              />
            ) : (
              <HubShowMoreGrid
                items={gridList}
                compactRows={hoursHub}
                linkSearch={linkSearch}
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={Number(filters.page_size ?? 12)}
                filters={filters}
                mapOn={mapOn}
                lang={lang}
                markUnbookable={isBookableFirstHub}
                phoneMap={{
                  onClose: () => setMapWanted(false),
                  categorySlug: params.category,
                  citySlug: params.city,
                  collections:
                    withCollections ? { counts: tagCounts, active: filters.tag, onChange: setTag } : undefined,
                }}
              />
            )}
          </div>
          </HubFade>
        </div>
        </HubSlide>

        <HubFloatingFilters
          bounds={priceBounds}
          value={{
            min: search.min_price !== undefined && search.min_price !== "" ? Number(search.min_price) : undefined,
            max: search.max_price !== undefined && search.max_price !== "" ? Number(search.max_price) : undefined,
            sort: activeSort,
            show: showFilter,
            ...(params.category === "stays" ? { stay: stayFilters } : {}),
            ...(chipGroups
              ? {
                  chips: {
                    open: when.open ? "1" : undefined,
                    tonight: when.tonight ? "1" : undefined,
                    tag: filters.tag,
                    cuisine: filters.cuisine,
                  },
                }
              : {}),
          }}
          staySection={isStays ? { withDates: stayDatesChosen, count: countStays } : undefined}
          audience={showAudienceFilter}
          // Restaurant tables are free to book, so there is no price to filter
          // or sort by (owner, 2026-10-01).
          showPrice={!noPriceHub}
          staticCount={total}
          sortOptions={sortOptions}
          chipGroups={chipGroups}
          countFor={hoursHub ? countFor : undefined}
          baseFilters={countBase}
          onApply={applyHubFilters}
          floatingExtra={canMap ? <StaysMapToggle on={mapOn} onChange={setMapWanted} variant="floating" /> : undefined}
          priceLabel={
            params.category === "stays" || isStayPropertyType(params.category ?? "")
              ? t("filter.price_per_night", { defaultValue: "Price per night" })
              : t("filter.price_per_person", { defaultValue: "Price per person" })
          }
        />

        {/* The hub's reading sections. Map view hides them (StaysHubGrid's
            map stylesheet), since that view is the list and the map alone;
            the server-rendered page always has them. */}
        <div className="mj-hub-extras">
        <HubIntro title={seo.kicker} text={seo.lede} />

        {isCityOverview && params.city ? (
          <CityExploreLinks
            citySlug={params.city}
            cityName={seo.cityName}
            lang={lang}
          />
        ) : null}

        <CategoryIntro sections={seo.sections} />

        <CategoryNeighborhoods
          cityName={seo.cityName}
          neighborhoods={seo.neighborhoods}
          bestTime={seo.bestTime}
        />

        <CategoryFaq faqs={seo.faqs} />

        <CategoryCityArticles cityName={seo.cityName} lang={lang} />

        <CategoryNearbyCities
          citySlug={params.city}
          cityName={seo.cityName}
          lang={lang}
          cities={cityRows}
        />

        {/* On an empty city hub these are "Spas in {city}", "Pools in {city}"
            and so on: links to more pages with nothing on them. */}
        {isEmptyCityHub ? null : (
          <CategoryRelated
            related={seo.related}
            cityName={seo.cityName}
            categoryName={seo.categoryName}
          />
        )}
        </div>
      </Container>
    </main>
  );
}

function ExploreSkeleton() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function ExplorePage() {
  return (
    <Suspense fallback={<ExploreSkeleton />}>
      <ExploreContent />
    </Suspense>
  );
}
